import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatPrice, formatMileage, formatDate, formatEzDate } from "../lib/api";
import InsuranceQuickEstimate from "../components/InsuranceQuickEstimate";
import TestRideRequestModal from "../components/listing/TestRideRequestModal";
import SellerEmailButton from "../components/listing/SellerEmailButton";
import LicenseClassBadges from "../components/listing/LicenseClassBadges";
import ListingQrCard from "../components/listing/ListingQrCard";
import { normalizeImageUrl } from "../lib/imageUrl";
import { previewPdfRequest } from "../lib/pdfPreview";
import TotalCostOfOwnershipWidget from "../components/TotalCostOfOwnershipWidget";
import FinancingCalculatorWidget from "../components/FinancingCalculatorWidget";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ReportListingDialog from "../components/ReportListingDialog";
import { Heart, MapPin, Calendar, Gauge, Zap, Phone, Mail, Send, Star, Shield, ArrowLeft, ChevronLeft, ChevronRight, Gavel, FileText, BadgeCheck, Wrench, Award, KeyRound, Video, Bike, QrCode, ShieldCheck, Lock, Download, Share2, Copy as CopyIcon, Check, AlertTriangle, ChevronDown, ChevronUp, Eye, Printer, Bookmark, RefreshCw, Truck, MessageCircle, Facebook, Linkedin, Twitter, Sparkles, Loader2, Instagram, Music2, Layers, ShoppingBag } from "lucide-react";
import { useAuth } from "../lib/auth";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import AuctionPanel from "../components/AuctionPanel";
import OfferPanel from "../components/OfferPanel";
import TradeInPanel from "../components/TradeInPanel";
import ReservationPanel from "../components/ReservationPanel";
import CallbackRequestButton from "../components/CallbackRequestButton";
import TrackingNumberManager from "../components/TrackingNumberManager";
import SponsoredAdSlot from "../components/SponsoredAdSlot";

// Mai-2026 (User-Wunsch 21 May Iteration 14) — Telefon-Maskierung: zeigt
// die ersten 6 Zeichen sichtbar (z. B. Vorwahl + 2 Ziffern) und ersetzt den Rest
// durch Bullet-Punkte. Wird nur in der Anzeige verwendet; der gespeicherte Wert
// bleibt unverändert.
const maskPhone = (phone) => {
    const str = String(phone || "");
    if (str.length <= 6) return str;
    const head = str.slice(0, Math.ceil(str.length / 2));
    const tailLen = str.length - head.length;
    return head + "•".repeat(Math.min(tailLen, 6));
};

export default function ListingDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [listing, setListing] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [favorited, setFavorited] = useState(false);
    // Mai-2026 (User-Wunsch 21 May Iteration 14) — Telefon-Aufdecken-Toggle.
    const [phoneRevealed, setPhoneRevealed] = useState(false);
    const [showAllSpecs, setShowAllSpecs] = useState(false); // Jul-2026: Technische Daten auf-/zuklappen
    const [tcoMonthly, setTcoMonthly] = useState(0);
    const [testRideModalOpen, setTestRideModalOpen] = useState(false);
    const [descLang, setDescLang] = useState("de");
    const [descTranslating, setDescTranslating] = useState(false);
    const [descTranslated, setDescTranslated] = useState(null);
    // Phase 2 — Impressum-Modal
    const [imprintOpen, setImprintOpen] = useState(false);
    const [imprintData, setImprintData] = useState(null);
    // Phase 3.90 — Unfallfahrzeug-Banner ausklappbar
    const [damageOpen, setDamageOpen] = useState(false);
    // Jun-2026 (User-Wunsch) — Ähnliche Fahrzeuge unter dem „VERKAUFT"-Banner (Cross-Selling)
    const [similar, setSimilar] = useState([]);
    // Jun-2026 (User-Wunsch) — Zubehör-Schaufenster: lagerndes Zubehör des Händlers.
    const [accessories, setAccessories] = useState([]);
    const [selectedAcc, setSelectedAcc] = useState([]);
    // Jul-2026 (User-Wunsch) — Preisvorteil-Aktionen des Anbieters im Inserat anzeigen
    const [priceAdvantages, setPriceAdvantages] = useState([]);
    useEffect(() => {
        let alive = true;
        api.get(`/listings/${id}/price-advantages`)
            .then(({ data }) => { if (alive) setPriceAdvantages(data?.items || []); })
            .catch(() => { if (alive) setPriceAdvantages([]); });
        return () => { alive = false; };
    }, [id]);
    const openImprint = async () => {
        try {
            if (!imprintData) {
                const { data } = await api.get(`/listings/${id}/imprint`);
                setImprintData(data);
            }
            setImprintOpen(true);
        } catch (e) {
            toast.error("Impressum konnte nicht geladen werden");
        }
    };

    const refresh = useCallback(async () => {
        const { data } = await api.get(`/listings/${id}`);
        setListing(data);
        // Track for the homepage "Zuletzt angesehen" section (local, no login needed)
        try {
            const { addRecentlyViewed } = await import("../lib/recentlyViewed");
            addRecentlyViewed(data);
        } catch { /* non-fatal */ }
    }, [id]);

    useEffect(() => {
        (async () => {
            await refresh();
            if (user && user !== false) {
                try {
                    const ids = await api.get("/favorites/ids");
                    setFavorited(ids.data.includes(id));
                } catch (e) {
                    console.error("Failed to load favorite ids", e);
                }
                // Phase 4.14 (L2 Tracking) — View persistieren
                api.post(`/listings/${id}/track-view`).catch(() => { /* silent */ });
            }
        })();
    }, [id, user, refresh]);

    // Jun-2026 (User-Wunsch) — „Mehr von diesem Händler" dauerhaft auf jeder
    // Detailseite laden (inkl. reservierter/verkaufter Fahrzeuge → Cross-Selling).
    useEffect(() => {
        let alive = true;
        api.get(`/listings/${id}/similar`, { params: { limit: 6 } })
            .then(({ data }) => { if (alive) setSimilar(data?.items || []); })
            .catch(() => { if (alive) setSimilar([]); });
        return () => { alive = false; };
    }, [id]);

    // Jun-2026 (User-Wunsch) — Zubehör-Schaufenster laden (öffentlich).
    useEffect(() => {
        let alive = true;
        api.get(`/listings/${id}/accessory-showcase`)
            .then(({ data }) => { if (alive) setAccessories(data?.items || []); })
            .catch(() => { if (alive) setAccessories([]); });
        return () => { alive = false; };
    }, [id]);
    useEffect(() => {
        if (!listing) return;
        const seo = listing.seo_meta || {};
        const title = seo.meta_title || `${listing.brand} ${listing.model} ${listing.year || ""} · TwoWheels`.trim();
        const desc = seo.meta_description || `${listing.brand} ${listing.model}${listing.price ? ` ab ${Number(listing.price).toLocaleString("de-DE")} €` : ""} auf twowheels.info`;
        const keywords = (seo.meta_keywords && seo.meta_keywords.join(", ")) || `${listing.brand}, ${listing.model}, Motorrad, gebraucht`;
        const oldTitle = document.title;
        document.title = title;
        const upsertMeta = (name, content) => {
            let m = document.querySelector(`meta[name="${name}"]`);
            if (!m) {
                m = document.createElement("meta");
                m.setAttribute("name", name);
                document.head.appendChild(m);
            }
            m.setAttribute("content", content);
            return m;
        };
        const mDesc = upsertMeta("description", desc);
        const mKw = upsertMeta("keywords", keywords);
        // og: tags
        const upsertOg = (prop, content) => {
            let m = document.querySelector(`meta[property="${prop}"]`);
            if (!m) {
                m = document.createElement("meta");
                m.setAttribute("property", prop);
                document.head.appendChild(m);
            }
            m.setAttribute("content", content);
            return m;
        };
        const mOgT = upsertOg("og:title", title);
        const mOgD = upsertOg("og:description", desc);
        // Phase 4.16 — Dynamische OG-Vorschau aus Backend (Branded + Preis-Overlay)
        const ogImgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/seo/og-image/${listing.id}`;
        const mOgI = upsertOg("og:image", ogImgUrl);
        const mOgIW = upsertOg("og:image:width", "1200");
        const mOgIH = upsertOg("og:image:height", "630");
        const mTwCard = upsertOg("twitter:card", "summary_large_image");
        const mTwImg = upsertOg("twitter:image", ogImgUrl);
        return () => {
            document.title = oldTitle;
            [mDesc, mKw, mOgT, mOgD, mOgI, mOgIW, mOgIH, mTwCard, mTwImg].forEach((el) => { if (el) el.remove(); });
        };
    }, [listing]);

    if (!listing) return <div className="max-w-7xl mx-auto px-4 py-12">Lädt...</div>;

    const owner = listing.owner;
    const isB2B = owner?.role === "b2b";
    const isAuction = listing.sale_type === "auction";
    const isPawn = listing.sale_type === "pawn";
    const isMoto = listing.category === "motorcycle";

    const colorFinishLabel = listing.color_finish === "matt" ? "matt" : listing.color_finish === "glaenzend" ? "glänzend" : null;
    const colorDisplay = [listing.color, colorFinishLabel].filter(Boolean).join(" · ") || null;

    const UMWELTPLAKETTE_LABEL = { rot: "Rot", gruen: "Grün", blau: "Blau", keine: "Keine", nicht_bekannt: "Nicht bekannt" };
    const WARRANTY_TYPE_LABEL = { hersteller: "Hersteller-Garantie", drittanbieter: "Drittanbieter-Garantie", gesetzlich: "Gesetzliche Gewährleistung" };
    const formatMonthYear = (v) => {
        if (!v) return null;
        const m = /^(\d{4})-(\d{2})/.exec(v);
        return m ? `${m[2]}/${m[1]}` : v;
    };

    // Feb-2026 (User-Wunsch 26 May v4): Privat-Felder (Fahrzeug-Klasse J, FIN,
    // TSN, Bemerkungen) sind sensible interne Identifizierungsdaten — nur dem
    // inserierenden Händler (oder Admin) zeigen.
    const isOwnerListing = !!(user && listing.owner_id === user.id);
    const isAdminUser = user?.role === "admin";
    const canSeePrivate = isOwnerListing || isAdminUser;

    const specs = isMoto
        ? [
              { label: "Fahrzeugtyp", value: listing.vehicle_type },
              canSeePrivate && { label: "Fahrzeug-Klasse (J)", value: listing.vehicle_class },
              { label: "Hersteller", value: listing.manufacturer },
              { label: "HSN (2.1)", value: listing.hsn },
              canSeePrivate && { label: "TSN (2.2)", value: listing.tsn },
              canSeePrivate && { label: "FIN (E)", value: listing.vin },
              { label: "Zustand", value: listing.condition },
              { label: "Erstzulassung", value: formatEzDate(listing.first_registration_date || listing.year) },
              { label: "Kilometerstand", value: formatMileage(listing.mileage) },
              { label: "Leistung", value: listing.power_hp ? `${listing.power_hp} PS (${listing.power_kw} kW)` : (listing.power_kw ? `${listing.power_kw} kW` : null) },
              { label: "Hubraum", value: listing.displacement ? `${listing.displacement} ccm` : null },
              { label: "Höchstgeschw. (T)", value: listing.max_speed_kmh ? `${listing.max_speed_kmh} km/h` : null },
              { label: "Zylinder", value: listing.cylinder_count ? `${listing.cylinder_count}` : null },
              { label: "Kraftstoff", value: listing.fuel },
              { label: "Antrieb", value: listing.drive_type },
              { label: "Schadstoffklasse", value: listing.emission_class },
              { label: "Umweltplakette", value: UMWELTPLAKETTE_LABEL[listing.umweltplakette] || null },
              { label: "Getriebe", value: listing.transmission },
              { label: "Sitzplätze", value: listing.seat_count ? String(listing.seat_count) : null },
              { label: "Sitzhöhe", value: listing.seat_height_mm ? `${listing.seat_height_mm} mm` : null },
              { label: "Tankinhalt", value: listing.tank_capacity_l ? `${String(listing.tank_capacity_l).replace(".", ",")} l` : null },
              { label: "Reifen vorne (15.1)", value: listing.tire_size_front },
              { label: "Reifen hinten (15.2)", value: listing.tire_size_rear },
              { label: "Länge (18)", value: listing.length_mm ? `${listing.length_mm} mm` : null },
              { label: "Breite (19)", value: listing.width_mm ? `${listing.width_mm} mm` : null },
              { label: "Höhe (20)", value: listing.height_mm ? `${listing.height_mm} mm` : null },
              { label: "Leergewicht (G)", value: listing.weight_kg ? `${listing.weight_kg} kg` : null },
              { label: "Zul. Gesamtgewicht (F.1)", value: listing.max_weight_kg ? `${listing.max_weight_kg} kg` : null },
              { label: "Farbe Hersteller", value: colorDisplay },
              { label: "Farbbasis", value: listing.color_base },
              { label: "Vorbesitzer", value: listing.previous_owners != null ? String(listing.previous_owners) : null },
              { label: "Garantie", value: listing.warranty_type ? `${WARRANTY_TYPE_LABEL[listing.warranty_type]}${listing.warranty_months ? ` · ${listing.warranty_months} Mon.` : ""}` : (listing.warranty_months ? `${listing.warranty_months} Monate` : null) },
              { label: "Letzte Wartung", value: formatMonthYear(listing.last_service_date) },
              { label: "TÜV bis", value: listing.tuev_neu ? "TÜV neu" : formatMonthYear(listing.tuev_until) },
              { label: "HU/AU bis", value: listing.next_inspection_date },
              { label: "Land der Zulassung", value: listing.country_of_registration },
              { label: "Anhängelast", value: listing.trailer_load_kg ? `${listing.trailer_load_kg} kg` : null },
              canSeePrivate && { label: "Bemerkungen (22)", value: listing.bemerkungen },
              // Elektro-spezifisch
              { label: "Reichweite", value: listing.range_km ? `${listing.range_km} km` : null },
              { label: "Ladedauer", value: listing.charging_time_hours ? `${listing.charging_time_hours} h` : null },
          ]
        : [
              { label: "Baujahr", value: listing.year },
              { label: "Kilometerstand", value: formatMileage(listing.mileage) },
              { label: "Typ", value: listing.bike_type },
              { label: "Rahmenmaterial", value: listing.frame_material },
              { label: "Rahmengröße", value: listing.frame_size },
              { label: "Laufradgröße", value: listing.wheel_size },
              { label: "Schaltung", value: listing.gears ? `${listing.gears}-Gang` : null },
              { label: "Antrieb", value: listing.drive_type },
              { label: "Farbe Hersteller", value: colorDisplay },
              { label: "Farbbasis", value: listing.color_base },
              { label: "Zustand", value: listing.condition },
              { label: "Vorbesitzer", value: listing.previous_owners != null ? String(listing.previous_owners) : null },
              { label: "Garantie", value: listing.warranty_type ? `${WARRANTY_TYPE_LABEL[listing.warranty_type]}${listing.warranty_months ? ` · ${listing.warranty_months} Mon.` : ""}` : (listing.warranty_months ? `${listing.warranty_months} Monate` : null) },
              { label: "Letzte Wartung", value: formatMonthYear(listing.last_service_date) },
          ];

    const trustBadges = [
        listing.accident_free === true && { icon: BadgeCheck, label: "Unfallfrei", testId: "badge-accident-free" },
        listing.has_service_book === true && { icon: Wrench, label: "Scheckheftgepflegt", testId: "badge-servicebook" },
        listing.workshop_inspected === true && { icon: Wrench, label: "Werkstatt-geprüft", testId: "badge-workshop-inspected" },
        listing.tuev_neu === true && { icon: Shield, label: "TÜV neu", testId: "badge-tuev-neu" },
        listing.warranty_months > 0 && { icon: Award, label: `Garantie ${listing.warranty_months} Mon.`, testId: "badge-warranty" },
        listing.emission_class && { icon: Shield, label: listing.emission_class, testId: "badge-emission" },
        listing.battery_certificate === true && { icon: Shield, label: "Batteriezertifikat", testId: "badge-battery-cert" },
        // Feb-2026 (User-Wunsch 26 May): MwSt-Status-Badge — bei `vat_extractable` bzw.
        // `vat_reportable` ausweisbar; sonst Differenzbesteuerung gem. § 25a UStG.
        (listing.vat_reportable === true || listing.vat_extractable === true)
            ? { icon: FileText, label: "MwSt. ausweisbar", testId: "badge-vat-reportable" }
            : ((listing.vat_reportable === false || listing.vat_extractable === false)
                ? { icon: FileText, label: "Differenzbesteuert (§ 25a UStG)", testId: "badge-vat-differential" }
                : null),
        // Feb-2026 (User-Wunsch 26 May v3): Übergabe-/Lieferungs-Optionen als
        // prominente Top-Trust-Badges (zusätzlich zur Detail-Sektion weiter
        // unten). So sieht der Käufer auf einen Blick, ob das Fahrzeug
        // sofort mitnehmbar ist.
        listing.pickup_available === true && { icon: BadgeCheck, label: "Sofortmitnahme / Abholung", testId: "badge-pickup-available-top" },
        listing.pickup_with_registration === true && { icon: BadgeCheck, label: "Noch zugelassen (eVB nötig)", testId: "badge-pickup-registered-top" },
        listing.pickup_unregistered_transport === true && { icon: Truck, label: "Auslieferungsfertig (Transport)", testId: "badge-pickup-transport-top" },
        listing.delivery_nationwide === true && { icon: Truck, label: "Bundesweite Lieferung", testId: "badge-delivery-nationwide-top" },
    ].filter(Boolean);

    const toggleFav = async () => {
        if (!user || user === false) {
            navigate("/login");
            return;
        }
        const { data } = await api.post(`/favorites/${id}`);
        setFavorited(data.favorited);
        toast(data.favorited ? "Zu Favoriten hinzugefügt" : "Aus Favoriten entfernt");
    };

    // Mai-2026 (User-Wunsch 21 May Iteration 14) — Schnellaktionen für Käufer-Buttons.
    // Sendet vorbefüllte Direktchat-Nachrichten (Probefahrt / Inzahlungnahme) oder
    // triggert externe Aktionen (E-Mail-Client, Druck, Parken, Teilen).
    const sendQuickMessage = async (text, successMsg) => {
        if (!user || user === false) { navigate("/login"); return; }
        if (!owner?.id) return;
        try {
            await api.post("/messages", { listing_id: id, to_user_id: owner.id, content: text });
            toast.success(successMsg || "Nachricht gesendet!");
        } catch {
            toast.error("Senden fehlgeschlagen");
        }
    };
    // Feb-2026 (User-Wunsch 26 May v2): Anonymes Probefahrt-Modal — der
    // „Probefahrtanfrage"-Button öffnet jetzt das Modal, auch für nicht
    // eingeloggte Käufer. Pflichtfelder Vorname/Name/E-Mail/Handy werden im
    // Modal abgefragt; Logged-in-User bekommen Default-Werte vorbefüllt.
    // Feb-2026 (User-Wunsch 27 May): UI-Trigger entfernt; Modal bleibt für
    // mögliche externe/Admin Flows verfügbar.
    // eslint-disable-next-line no-unused-vars
    const requestTestRide = () => {
        if (!owner?.id) return;
        setTestRideModalOpen(true);
    };
    const requestTradein = () => sendQuickMessage(
        `Guten Tag, ich interessiere mich für das Fahrzeug "${listing.title}" und möchte mein bisheriges Fahrzeug in Zahlung geben. Können Sie mir die Konditionen nennen? Danke.`,
        "Inzahlungnahme-Anfrage gesendet!",
    );
    const printOffer = () => {
        // Gebrandetes Angebots-PDF (Händler-Logo, Eckdaten, QR) in der globalen
        // Canvas-Vorschau anzeigen — versendbar, statt simpler Browser-Druckansicht.
        toast("Angebots-PDF wird erstellt…");
        previewPdfRequest(
            api.get(`/listings/${id}/offer.pdf`, { responseType: "blob" }),
            "Angebot.pdf",
        );
    };

    const sendMessage = async () => {
        if (!user || user === false) {
            navigate("/login");
            return;
        }
        // Jun-2026 (User-Wunsch) — gewähltes Zubehör wandert mit in die Anfrage.
        const base = message.trim() || (selectedAcc.length ? `Hallo, ich interessiere mich für ${[listing.brand, listing.model].filter(Boolean).join(" ") || "dieses Fahrzeug"}.` : "");
        if (!base) return;
        setSending(true);
        try {
            await api.post("/messages", {
                listing_id: id,
                to_user_id: owner.id,
                content: base + accLines,
            });
            toast.success("Nachricht gesendet!");
            setMessage("");
            setSelectedAcc([]);
        } catch (e) {
            toast.error("Senden fehlgeschlagen");
        } finally {
            setSending(false);        }
    };

    const toggleAccessory = (a) => {
        setSelectedAcc((prev) => {
            if (prev.some((x) => x.id === a.id)) return prev.filter((x) => x.id !== a.id);
            toast.success("Zubehör zur Anfrage hinzugefügt — im Kontaktbereich absenden");
            return [...prev, a];
        });
    };

    const accLines = selectedAcc.length
        ? "\n\nZubehör-Wunsch (bitte mit anbieten):\n" + selectedAcc.map((a) => `• ${a.name} — ${formatPrice(a.price_gross)} (UVP)`).join("\n")
        : "";

    const img = normalizeImageUrl(listing.images?.[imgIdx]);

    return (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10" data-testid="listing-detail-page">
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <Link to={-1} onClick={(e) => { e.preventDefault(); navigate(-1); }} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#111827]">
                    <ArrowLeft className="w-4 h-4" /> Zurück
                </Link>
                {user?.role === "admin" && (
                    <Link
                        to={`/admin/listing/${id}/log`}
                        className="inline-flex items-center gap-1 text-xs uppercase tracking-wider font-bold bg-[#111827] text-[#FFCC00] px-3 py-1.5 rounded-sm hover:bg-black"
                        data-testid="admin-log-link"
                    >
                        🛡 Admin-Protokoll
                    </Link>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left: Images + specs */}
                <div className="lg:col-span-2 space-y-8">
                    {/* Phase 3.25 — Werbung auf Fahrzeug-Detailseite */}
                    <SponsoredAdSlot variant="card" limit={1} />
                    {/* Image gallery */}
                    <div className="relative bg-gray-100 rounded-sm overflow-hidden">
                        <div className="aspect-[4/3]">
                            <img src={img} alt={listing.title} className="w-full h-full object-cover" data-testid="main-image" />
                        </div>
                        {listing.sold && (
                            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-4" data-testid="listing-sold-overlay">
                                <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                                <span className="relative -rotate-[18deg] bg-red-600 text-white font-black tracking-[0.2em] text-3xl md:text-5xl px-8 md:px-14 py-2 md:py-3 border-4 border-white shadow-2xl uppercase pointer-events-none">
                                    Verkauft
                                </span>
                                {similar.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => document.getElementById("similar-vehicles")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                                        className="relative inline-flex items-center gap-2 bg-white text-[#111827] hover:bg-[#FFCC00] font-bold text-sm px-5 py-2.5 rounded-sm shadow-lg transition-colors"
                                        data-testid="sold-similar-cta"
                                    >
                                        <Bike className="w-4 h-4" /> Ähnliche Fahrzeuge ansehen
                                    </button>
                                )}
                            </div>
                        )}
                        {listing.image_labels?.[imgIdx] && (
                            <div className="absolute top-3 left-3 bg-black/75 text-white text-[11px] font-semibold uppercase tracking-wider px-2 py-1 rounded-sm" data-testid="image-label-badge">
                                {listing.image_labels[imgIdx]}
                            </div>
                        )}
                        {(listing.symbol_image_urls || []).includes(listing.images?.[imgIdx]) && (
                            <div className="absolute top-3 right-3 bg-amber-500 text-black text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm" data-testid="symbol-photo-badge">
                                Symbolfoto
                            </div>
                        )}
                        {listing.images?.length > 1 && (
                            <>
                                <button
                                    onClick={() => setImgIdx((i) => (i === 0 ? listing.images.length - 1 : i - 1))}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white w-10 h-10 rounded-sm flex items-center justify-center"
                                    data-testid="prev-image"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => setImgIdx((i) => (i + 1) % listing.images.length)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white w-10 h-10 rounded-sm flex items-center justify-center"
                                    data-testid="next-image"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                                <div className="absolute bottom-3 left-3 bg-black/70 text-white text-xs font-mono-num px-2 py-1 rounded-sm">
                                    {imgIdx + 1} / {listing.images.length}
                                </div>
                            </>
                        )}
                    </div>

                    {listing.images?.length > 1 && (
                        <div className="flex gap-2 overflow-x-auto">
                            {listing.images.map((src, i) => (
                                <button
                                    key={src}
                                    onClick={() => setImgIdx(i)}
                                    className={`flex-shrink-0 w-20 h-20 border-2 rounded-sm overflow-hidden ${i === imgIdx ? "border-[#111827]" : "border-transparent opacity-60"}`}
                                >
                                    <img src={normalizeImageUrl(src)} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Title + price */}
                    <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{listing.brand}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-xs font-semibold text-gray-500">{isMoto ? "Motorrad" : "Fahrrad"}</span>
                            <Badge
                                className={isB2B ? "bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide font-bold rounded-sm ml-2" : "bg-gray-200 text-gray-800 hover:bg-gray-200 uppercase tracking-wide font-bold rounded-sm ml-2"}
                            >
                                {isB2B ? "Händler" : "Privat"}
                            </Badge>
                            {isAuction && (
                                <Badge className="bg-[#FFCC00] text-[#111827] hover:bg-[#FFCC00] uppercase tracking-wide font-bold rounded-sm ml-1 gap-1">
                                    <Gavel className="w-3 h-3" /> Auktion
                                </Badge>
                            )}
                            {/* Feb-2026 (User-Wunsch 24 May Iter 14): Angebotsnummer
                                prominent neben Marke/Typ — gibt Käufer eine
                                stabile Referenz für Rückfragen und Reklamationen. */}
                            {listing.internal_number && (
                                <span
                                    className="ml-auto inline-flex items-center gap-1 bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num text-[11px] font-bold tracking-wider"
                                    title="Einmalige und unveränderliche Angebotsnummer"
                                    data-testid="detail-internal-number"
                                >
                                    Nr. {listing.internal_number}
                                </span>
                            )}
                            {/* Feb-2026 (User-Wunsch 24 May Iter 17): QR-Scan-Badge —
                                nur für den Inserats-Eigentümer sichtbar. */}
                            {user?.id && listing.owner_id === user.id && (listing.qr_scan_count || 0) > 0 && (
                                <span
                                    className="inline-flex items-center gap-1 bg-[#FFCC00] text-[#111827] px-2 py-0.5 rounded-sm font-mono-num text-[11px] font-bold tracking-wider"
                                    title="QR-Code-Scans auf den watermarkten Bildern"
                                    data-testid="detail-qr-scans"
                                >
                                    <QrCode className="w-3 h-3" />
                                    {listing.qr_scan_count} Scans
                                </span>
                            )}
                        </div>
                        <h1 className="font-display text-2xl md:text-4xl font-black tracking-tight mb-4 leading-tight" data-testid="detail-title">{listing.title}</h1>
                        {listing.is_top && listing.top_until && new Date(listing.top_until) > new Date() && (
                            <div className="inline-flex items-center gap-1.5 bg-[#DC2626] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm mb-3" data-testid="detail-top-badge">
                                <Star className="w-3 h-3" fill="currentColor" /> Top-Inserat
                            </div>
                        )}
                        {typeof listing.match_score === "number" && listing.match_score > 0 && (
                            <div
                                className={`inline-flex items-center gap-1.5 ml-2 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-sm mb-3 ${
                                    listing.match_score >= 80
                                        ? "bg-green-600 text-white"
                                        : listing.match_score >= 60
                                        ? "bg-[#FFCC00] text-[#111827]"
                                        : listing.match_score >= 40
                                        ? "bg-orange-400 text-white"
                                        : "bg-gray-500 text-white"
                                }`}
                                data-testid="detail-match-score"
                                title="Treffergrad zu Ihren gespeicherten Suchen"
                            >
                                {Math.round(listing.match_score)}% Match
                            </div>
                        )}
                        <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-700 mb-4">
                            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-gray-400" /> EZ {formatEzDate(listing.first_registration_date || listing.year)}</span>
                            <span className="flex items-center gap-1.5"><Gauge className="w-4 h-4 text-gray-400" /> {formatMileage(listing.mileage)}</span>
                            {listing.power_hp && <span className="flex items-center gap-1.5"><Zap className="w-4 h-4 text-gray-400" /> {listing.power_hp} PS</span>}
                            <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gray-400" /> {[listing.street, listing.location].filter(Boolean).join(", ")}</span>
                        </div>
                        {trustBadges.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-6" data-testid="trust-badges">
                                {trustBadges.map((b) => {
                                    const Icon = b.icon;
                                    return (
                                        <span key={b.label} className="inline-flex items-center gap-1.5 bg-green-50 text-green-800 border border-green-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider" data-testid={b.testId}>
                                            <Icon className="w-3.5 h-3.5" /> {b.label}
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Phase 3.90 — Unfallfahrzeug-Transparenz-Banner */}
                        {listing.is_accident_vehicle === true && (
                            <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-sm overflow-hidden" data-testid="accident-banner">
                                <button
                                    type="button"
                                    onClick={() => setDamageOpen((v) => !v)}
                                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-100/40"
                                    data-testid="accident-banner-toggle"
                                    aria-expanded={damageOpen}
                                >
                                    <AlertTriangle className="w-5 h-5 text-red-700 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-bold text-sm text-red-900">Unfallfahrzeug</div>
                                        <div className="text-[11px] text-red-800/80 mt-0.5">Der Anbieter hat dieses Fahrzeug als Unfallfahrzeug deklariert — Details ansehen.</div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        {listing.is_roadworthy === false && (
                                            <span className="inline-flex items-center bg-red-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5">Nicht fahrtauglich</span>
                                        )}
                                        {listing.has_tipover_damage === true && (
                                            <span className="inline-flex items-center bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-0.5">UM-Kippschäden</span>
                                        )}
                                        <ChevronDown className={`w-4 h-4 text-red-700 transition-transform ${damageOpen ? "rotate-180" : ""}`} />
                                    </div>
                                </button>
                                {damageOpen && (
                                    <div className="px-4 py-3 border-t border-red-200 bg-white text-sm text-gray-800" data-testid="accident-banner-body">
                                        {listing.damage_info ? (
                                            <>
                                                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Schadensinformationen vom Anbieter</div>
                                                <p className="whitespace-pre-line leading-relaxed">{listing.damage_info}</p>
                                            </>
                                        ) : (
                                            <p className="text-gray-500 italic">Der Anbieter hat keine zusätzlichen Schadensinformationen hinterlegt. Bitte direkt im Chat nachfragen.</p>
                                        )}
                                        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider ${listing.is_roadworthy !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                                                {listing.is_roadworthy !== false ? "Fahrtauglich" : "Nicht fahrtauglich"}
                                            </span>
                                            {listing.has_tipover_damage && (
                                                <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-900 border border-amber-200 px-2 py-0.5 rounded-sm font-bold uppercase tracking-wider">UM-Kippschäden</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!isAuction && (
                            <div className="flex items-end justify-between border-t border-gray-100 pt-6 gap-3 flex-wrap">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gray-500 font-bold mb-1">{isPawn ? "Auszahlung" : "Preis"}</div>
                                    <div className="font-display font-black text-4xl md:text-5xl text-[#111827] font-mono-num">{formatPrice(isPawn ? listing.pawn_payout_eur : listing.price)}</div>
                                    {!isPawn && listing.price_reference?.is_reduction && (
                                        <div className="mt-1.5" data-testid="detail-price-reduction">
                                            <span className="text-base text-gray-500 line-through font-mono-num">{formatPrice(listing.price_reference.lowest_30d)}</span>
                                            <span className="ml-2 inline-flex items-center rounded-sm bg-red-100 text-red-700 px-1.5 py-0.5 text-xs font-bold">−{listing.price_reference.savings_pct}%</span>
                                            <div className="text-[11px] text-gray-500 mt-0.5">{listing.price_reference.note} (PAngV)</div>
                                        </div>
                                    )}
                                    {isPawn ? (
                                        <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5" data-testid="detail-pawn-terms">
                                            {listing.pawn_term_months ? <span>Laufzeit: <strong>{listing.pawn_term_months} Monate</strong></span> : null}
                                            {listing.pawn_buyback_eur ? <span>Rückkaufpreis: <strong>{formatPrice(listing.pawn_buyback_eur)}</strong></span> : null}
                                        </div>
                                    ) : (
                                        <div className="text-xs text-gray-500 mt-1">inkl. MwSt. · zzgl. Überführungskosten</div>
                                    )}
                                    {priceAdvantages.length > 0 && (
                                        <div className="mt-2.5 space-y-1.5 max-w-md" data-testid="detail-price-advantages">
                                            {priceAdvantages.map((a, i) => (
                                                <div key={i} className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-sm px-3 py-2 text-sm text-amber-900" data-testid={`detail-price-advantage-${i}`}>
                                                    <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                                                    <span>
                                                        <strong>Dein Preisvorteil für {(a.name || "").toUpperCase()} beträgt {a.amount_type === "percent" ? `${a.amount} %` : formatPrice(a.amount)}.</strong>
                                                        {a.valid_to ? <span className="text-xs text-amber-700"> Gültig bis {formatDate(a.valid_to)}.</span> : null}
                                                        {a.action_url ? <a href={a.action_url} target="_blank" rel="noreferrer" className="ml-1 underline text-xs" data-testid={`detail-price-advantage-link-${i}`}>Zur Aktion</a> : null}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 flex-wrap">
                                    <ShareButton listing={listing} user={user} />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={printOffer}
                                        className="rounded-sm border-2 border-[#111827]"
                                        data-testid="detail-print-offer-btn"
                                    >
                                        <Printer className="w-4 h-4 mr-2" /> Angebot drucken
                                    </Button>
                                    <EmailOfferButton listingId={id} user={user} />
                                    <Button
                                        variant="outline"
                                        onClick={toggleFav}
                                        className={`rounded-sm border-2 ${favorited ? "bg-[#FFCC00] border-[#FFCC00] text-[#111827]" : "border-[#111827]"}`}
                                        data-testid="detail-favorite-btn"
                                    >
                                        <Heart className="w-4 h-4 mr-2" fill={favorited ? "currentColor" : "none"} />
                                        {favorited ? "Gemerkt" : "Merken"}
                                    </Button>
                                </div>
                                <div className="mt-2 flex justify-end">
                                    <ReportListingDialog listingId={id} />
                                </div>
                            </div>
                        )}
                        {/* Phase 4.21 — Versicherungs-Schnellberechnung (alle Besucher außer B2B/Hersteller).
                            Feb-2026 (User-Wunsch 26 May): Für eingeloggte Händler/Hersteller-Konten
                            sind diese Privatkunden-Tools nicht relevant — daher ausblenden. */}
                        {!(user?.role === "b2b" || user?.role === "manufacturer" || user?.role === "hersteller") && (
                        <div className="border-t border-gray-100 pt-4 mt-4 space-y-3">
                            <InsuranceQuickEstimate listing={listing} />
                            <TotalCostOfOwnershipWidget listing={listing} onTotalMonthlyChange={setTcoMonthly} />
                            <FinancingCalculatorWidget listing={listing} tcoMonthly={tcoMonthly} />
                        </div>
                        )}
                        {/* Phase 4.19 — B2B-Only Preisblock (Händlerpreis, Auktions-Startpreis, Mindestpreis) */}
                        {(user?.role === "b2b" || user?.role === "admin") && (listing.price_b2b || listing.starting_price || listing.reserve_price) && (
                            <div className="border-t border-gray-100 pt-4 mt-4" data-testid="detail-b2b-only-prices">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-[#111827] text-[#FFCC00] rounded-sm">Nur B2B-Händler</span>
                                    <span className="text-xs text-gray-500">Diese Preise sind für Privatkunden ausgeblendet.</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                    {listing.price_b2b && (
                                        <div className="border border-gray-200 rounded-sm p-3 bg-gray-50" data-testid="b2b-price-haendler">
                                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Händlerpreis</div>
                                            <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(listing.price_b2b)}</div>
                                        </div>
                                    )}
                                    {/* Feb-2026 (User-Wunsch 27.05.2026) — Vermittlungsprovision neben Händlerpreis */}
                                    {listing.referral_commission_eur != null && listing.referral_commission_eur > 0 && (
                                        <div className="border-2 border-[#FFCC00] rounded-sm p-3 bg-[#FFCC00]/10" data-testid="b2b-price-vermittlung">
                                            <div className="text-[10px] uppercase tracking-wider text-amber-900 font-bold flex items-center gap-1">
                                                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600" />
                                                Vermittlungsprovision
                                            </div>
                                            <div className="font-display font-black text-2xl text-amber-900 font-mono-num">{formatPrice(listing.referral_commission_eur)}</div>
                                            <div className="text-[10px] text-amber-800/80 mt-0.5">Differenz B2C − B2B</div>
                                        </div>
                                    )}
                                    {listing.starting_price && (
                                        <div className="border border-gray-200 rounded-sm p-3 bg-gray-50" data-testid="b2b-price-auction-start">
                                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Auktions-Startpreis</div>
                                            <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(listing.starting_price)}</div>
                                        </div>
                                    )}
                                    {listing.reserve_price && (
                                        <div className="border border-gray-200 rounded-sm p-3 bg-gray-50" data-testid="b2b-price-reserve">
                                            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Mindestpreis (Reserve)</div>
                                            <div className="font-display font-black text-2xl text-[#111827] font-mono-num">{formatPrice(listing.reserve_price)}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                        {isAuction && (
                            <div className="border-t border-gray-100 pt-6 flex justify-end gap-2 flex-wrap">
                                <ShareButton listing={listing} user={user} />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={printOffer}
                                    className="rounded-sm border-2 border-[#111827]"
                                    data-testid="detail-print-offer-btn-auction"
                                >
                                    <Printer className="w-4 h-4 mr-2" /> Angebot drucken
                                </Button>
                                <EmailOfferButton listingId={id} user={user} />
                                <Button
                                    variant="outline"
                                    onClick={toggleFav}
                                    className={`rounded-sm border-2 ${favorited ? "bg-[#FFCC00] border-[#FFCC00] text-[#111827]" : "border-[#111827]"}`}
                                    data-testid="detail-favorite-btn"
                                >
                                    <Heart className="w-4 h-4 mr-2" fill={favorited ? "currentColor" : "none"} />
                                    {favorited ? "Gemerkt" : "Merken"}
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Spec table */}
                    <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8">
                        <h2 className="font-display text-2xl font-black mb-5 tracking-tight">Technische Daten</h2>
                        {(() => {
                            const visibleSpecs = specs.filter((s) => s && s.value);
                            // Jul-2026 (User-Wunsch) — Kurzansicht (wichtigste Felder) + Aufklappen.
                            const PRIMARY = ["Zustand", "Erstzulassung", "Kilometerstand", "Leistung", "Hubraum", "Zylinder", "Kraftstoff", "Getriebe", "Farbe Hersteller", "Farbbasis"];
                            const usePrimary = isMoto && visibleSpecs.some((s) => PRIMARY.includes(s.label));
                            const primarySpecs = usePrimary ? visibleSpecs.filter((s) => PRIMARY.includes(s.label)) : visibleSpecs;
                            const restSpecs = usePrimary ? visibleSpecs.filter((s) => !PRIMARY.includes(s.label)) : [];
                            const shown = showAllSpecs ? [...primarySpecs, ...restSpecs] : primarySpecs;
                            return (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                                        {shown.map((s) => (
                                            <div key={s.label} className="flex justify-between py-3 border-b border-gray-100 text-sm">
                                                <span className="text-gray-500 font-medium">{s.label}</span>
                                                <span className="font-semibold text-[#111827] text-right">{s.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {restSpecs.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAllSpecs((v) => !v)}
                                            className="mt-5 w-full flex items-center justify-between gap-2 px-4 py-3 rounded-sm border border-gray-200 bg-gray-50 hover:bg-gray-100 text-sm font-black uppercase tracking-wider text-[#111827] transition-colors"
                                            data-testid="listing-specs-toggle"
                                            aria-expanded={showAllSpecs}
                                        >
                                            <span className="inline-flex items-center gap-2">
                                                <Layers className="w-4 h-4 text-gray-500" />
                                                Weitere Stammdaten ({restSpecs.length})
                                            </span>
                                            {showAllSpecs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                        </button>
                                    )}
                                </>
                            );
                        })()}
                    </div>

                    {/* Phase 3.57 — Ausstattung & Zubehör als Chip-Reihen */}
                    {(listing.features?.length > 0 || listing.accessories?.length > 0) && (
                        <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8" data-testid="features-accessories-section">
                            {listing.features?.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Ausstattung</h3>
                                    <div className="flex flex-wrap gap-1.5" data-testid="features-chips">
                                        {listing.features.map((f) => (
                                            <span key={f} className="inline-flex items-center bg-gray-100 text-[#111827] border border-gray-200 rounded-sm px-2.5 py-1 text-xs font-semibold">{f}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {listing.accessories?.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Zubehör</h3>
                                    <div className="flex flex-wrap gap-1.5" data-testid="accessories-chips">
                                        {listing.accessories.map((a) => (
                                            <span key={a} className="inline-flex items-center bg-[#FFCC00]/15 text-[#111827] border border-[#FFCC00]/40 rounded-sm px-2.5 py-1 text-xs font-semibold">{a}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Description — Jul-2026 (User-Wunsch): gesamter Bereich nur, wenn Text vorhanden. */}
                    {(listing.description || "").trim() && (
                    <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8">
                        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
                            <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
                                Beschreibung
                                {(listing.ai_generated_fields || []).includes("description") && (
                                    <span
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-violet-100 text-violet-800 border border-violet-200"
                                        data-testid="ai-badge-description"
                                        title={`KI-generiert${listing.ai_source ? ` (Quelle: ${listing.ai_source === "manufacturer" ? "Hersteller" : "Internet"})` : ""}`}
                                    >
                                        ✨ KI-generiert
                                    </span>
                                )}
                            </h2>
                            {/* Phase 4.14 (L2 Translation) — Sprach-Switcher.
                                Jul-2026 (User-Wunsch): ausblenden, wenn keine Beschreibung vorhanden. */}
                            {(listing.description || "").trim() && (
                            <div className="flex items-center gap-1" data-testid="desc-lang-switcher">
                                {[
                                    { code: "de", label: "DE" },
                                    { code: "en", label: "EN" },
                                    { code: "fr", label: "FR" },
                                    { code: "it", label: "IT" },
                                ].map(({ code, label }) => {
                                    const isActive = descLang === code;
                                    const isCached = code !== "de" && !!(listing.translations || {})[code];
                                    return (
                                        <button
                                            key={code}
                                            type="button"
                                            onClick={async () => {
                                                if (code === "de") {
                                                    setDescLang("de");
                                                    setDescTranslated(null);
                                                    return;
                                                }
                                                if ((listing.translations || {})[code]) {
                                                    setDescLang(code);
                                                    setDescTranslated((listing.translations || {})[code]);
                                                    return;
                                                }
                                                setDescTranslating(true);
                                                try {
                                                    const { data } = await api.post(`/listings/${listing.id}/ai-translate`, { target: code });
                                                    if (data.description) {
                                                        setDescLang(code);
                                                        setDescTranslated(data);
                                                    } else {
                                                        toast.warning("Übersetzung nicht verfügbar.");
                                                    }
                                                } catch (e) {
                                                    toast.error(e?.response?.data?.detail || "Übersetzung fehlgeschlagen");
                                                } finally {
                                                    setDescTranslating(false);
                                                }
                                            }}
                                            disabled={descTranslating}
                                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border ${isActive ? "bg-[#111827] text-white border-[#111827]" : isCached ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"} disabled:opacity-50`}
                                            data-testid={`desc-lang-${code}`}
                                            title={isCached ? "Übersetzung gecached" : code === "de" ? "Original" : "KI-Übersetzung anfordern"}
                                        >
                                            {label}{descTranslating && isActive ? "…" : ""}
                                        </button>
                                    );
                                })}
                            </div>
                            )}
                        </div>
                        <p className="text-gray-700 whitespace-pre-line leading-relaxed" data-testid="listing-description-text">
                            {descTranslated?.description || listing.description}
                        </p>
                        {descTranslated && (
                            <p className="text-[10px] text-gray-400 mt-2 italic">
                                ✨ KI-Übersetzung — keine juristisch verbindliche Zusicherung.
                            </p>
                        )}
                    </div>
                    )}

                    {/* §479 BGB — Garantieerklärung: bei beworbener gewerblicher Garantie
                        (Hersteller/Drittanbieter) Pflichtangaben + Hinweis, dass die
                        gesetzliche Gewährleistung unberührt bleibt. */}
                    {(listing.warranty_type === "hersteller" || listing.warranty_type === "drittanbieter") && (
                        <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8" data-testid="warranty-479-section">
                            <h2 className="font-display text-2xl font-black mb-4 tracking-tight flex items-center gap-2">
                                <Award className="w-5 h-5 text-[#C2410C]" /> Garantie
                            </h2>
                            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                                <div className="flex justify-between sm:block border-b border-gray-100 pb-1.5">
                                    <dt className="text-gray-500">Garantiegeber</dt>
                                    <dd className="font-bold text-gray-900">{WARRANTY_TYPE_LABEL[listing.warranty_type]}</dd>
                                </div>
                                {listing.warranty_months > 0 && (
                                    <div className="flex justify-between sm:block border-b border-gray-100 pb-1.5">
                                        <dt className="text-gray-500">Garantiedauer</dt>
                                        <dd className="font-bold text-gray-900">{listing.warranty_months} Monate ab Übergabe</dd>
                                    </div>
                                )}
                            </dl>
                            <p className="text-xs text-gray-600 leading-relaxed mt-4 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="warranty-479-statutory-note">
                                <b>Wichtiger Hinweis (§ 479 BGB):</b> Diese Garantie ist eine zusätzliche, freiwillige Leistung und
                                lässt Ihre <b>gesetzlichen Gewährleistungsrechte unberührt</b> – sie schränkt diese nicht ein und sind
                                für Sie unentgeltlich. Die <b>vollständigen Garantiebedingungen</b> (Inhalt, räumlicher Geltungsbereich,
                                Name &amp; Anschrift des Garantiegebers, Verfahren zur Geltendmachung) erhalten Sie vom Händler in
                                Textform vor Vertragsschluss.
                            </p>
                        </div>
                    )}
                    {(() => {
                        // Jul-2026 (User-Wunsch) — Service & Wartung: strukturierte
                        // Felder anzeigen, sobald befüllt (nicht nur Freitext-Notiz).
                        const warrantyTxt = listing.warranty_type && listing.warranty_type !== "gesetzlich"
                            ? `${WARRANTY_TYPE_LABEL[listing.warranty_type] || listing.warranty_type}${listing.warranty_months ? ` · ${listing.warranty_months} Mon.` : ""}`
                            : (listing.warranty_months ? `${listing.warranty_months} Monate` : null);
                        const badges = [
                            listing.has_service_book && "Scheckheft gepflegt",
                            listing.workshop_inspected && "Werkstatt-geprüft",
                            listing.tuev_neu && "TÜV/HU neu",
                        ].filter(Boolean);
                        const rows = [
                            warrantyTxt && ["Garantie", warrantyTxt],
                            listing.last_service_date && ["Letzter Service", formatEzDate(listing.last_service_date)],
                            (listing.tuev_until || listing.next_inspection_date) && ["TÜV/HU bis", formatEzDate(listing.tuev_until || listing.next_inspection_date)],
                        ].filter(Boolean);
                        const hasSvc = badges.length || rows.length || listing.service_notes;
                        if (!hasSvc) return null;
                        return (
                            <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8" data-testid="service-notes-section">
                                <h2 className="font-display text-2xl font-black mb-4 tracking-tight flex items-center gap-2">
                                    <Wrench className="w-5 h-5 text-gray-500" /> Service &amp; Wartung
                                </h2>
                                {badges.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mb-4" data-testid="service-badges">
                                        {badges.map((b) => (
                                            <span key={b} className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider">
                                                <BadgeCheck className="w-3.5 h-3.5" /> {b}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {rows.length > 0 && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 mb-4" data-testid="service-fields">
                                        {rows.map(([label, value]) => (
                                            <div key={label} className="flex justify-between py-3 border-b border-gray-100 text-sm">
                                                <span className="text-gray-500 font-medium">{label}</span>
                                                <span className="font-semibold text-[#111827] text-right">{value}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {listing.service_notes && (
                                    <p className="text-gray-700 whitespace-pre-line leading-relaxed">{listing.service_notes}</p>
                                )}
                            </div>
                        );
                    })()}

                    {/* Phase 4.10 — Übergabe & Lieferung Optionen (öffentlich sichtbar) */}
                    {(listing.pickup_available || listing.delivery_nationwide) && (
                        <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8" data-testid="pickup-delivery-section">
                            <h2 className="font-display text-2xl font-black mb-4 tracking-tight">Übergabe &amp; Lieferung</h2>
                            <div className="flex flex-wrap gap-2">
                                {listing.pickup_available && (
                                    <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider" data-testid="badge-pickup-available">
                                        <BadgeCheck className="w-3.5 h-3.5" /> Sofortmitnahme möglich
                                    </span>
                                )}
                                {listing.pickup_with_registration && (
                                    <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-800 border border-gray-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider" data-testid="badge-pickup-registered">
                                        Mit Zulassung
                                    </span>
                                )}
                                {listing.pickup_unregistered_transport && (
                                    <span className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-800 border border-gray-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider" data-testid="badge-pickup-transport">
                                        Auslieferungsfertig (Transport)
                                    </span>
                                )}
                                {listing.delivery_nationwide && (
                                    <span className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-sm px-2.5 py-1 text-xs font-bold uppercase tracking-wider" data-testid="badge-delivery-nationwide">
                                        Bundesweite Lieferung möglich
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Phase 3.73 — Fahrzeug-QR-Code.
                        Feb-2026 (User-Wunsch 27.05.2026 — Refactoring P2):
                        Komponente ausgelagert nach
                        `components/listing/ListingQrCard.jsx`. Sichtbarkeit
                        siehe dort. */}
                    <ListingQrCard listing={listing} user={user} />

                    {/* Feb-2026 (User-Wunsch 27.05.2026) — Führerscheinklassen.
                        Berechnet aus displacement (ccm) + power_kw (kW) die
                        passenden DE-Führerscheinklassen (AM/A1/A2/A/B196). */}
                    <LicenseClassBadges
                        displacement={listing.displacement}
                        powerKw={listing.power_kw}
                        category={listing.category}
                    />

                    {/* Phase 3.75 — Portal-Daten (VIN, Papiere, Dokumente) bei Auktion/Direktkauf */}
                    {(listing.sale_type === "auction" || listing.direct_purchase) && (() => {
                        const isOwner = user && listing.owner_id === user.id;
                        const isAdmin = user && user.role === "admin";
                        const isPrivileged = isOwner || isAdmin;
                        const maskVin = (v) => v && v.length > 7 ? `${v.slice(0, 3)}${"•".repeat(v.length - 7)}${v.slice(-4)}` : v;
                        const hasAnyData = listing.vin || listing.key_count || listing.has_handsender || listing.has_keyless_transponder || listing.has_immobilizer_card || listing.has_coc || listing.has_zb1 || listing.has_zb2 || (listing.document_urls && listing.document_urls.length > 0);
                        if (!hasAnyData) return null;
                        return (
                            <div className="bg-white border-2 border-emerald-200 rounded-sm p-6 md:p-8" data-testid="portal-data-section">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShieldCheck className="w-5 h-5 text-emerald-600" />
                                    <h2 className="font-display text-2xl font-black tracking-tight">Portal-Transaktion: Detail-Daten</h2>
                                </div>
                                <p className="text-xs text-gray-600 mb-5">
                                    Diese Fahrzeugdetails werden bei einer Abwicklung über das Portal (Auktion / Direktkauf) bereitgestellt.
                                    {!user && <span className="text-amber-700 ml-1">Anmelden, um vollständige Angaben zu sehen.</span>}
                                </p>
                                {user ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {listing.vin && (
                                            <div data-testid="detail-vin">
                                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrgestellnummer (VIN)</Label>
                                                <div className="mt-1.5 font-mono-num text-sm font-bold text-[#111827] flex items-center gap-2">
                                                    {isPrivileged ? listing.vin : maskVin(listing.vin)}
                                                    {!isPrivileged && <Lock className="w-3.5 h-3.5 text-gray-400" title="Vollständige VIN nur für Käufer im Transaktionsprozess sichtbar" />}
                                                </div>
                                            </div>
                                        )}
                                        {(listing.key_count !== undefined && listing.key_count !== null) && (
                                            <div data-testid="detail-key-count">
                                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Anzahl der Schlüssel</Label>
                                                <div className="mt-1.5 font-bold text-[#111827] flex items-center gap-1.5">
                                                    <KeyRound className="w-4 h-4 text-gray-500" /> {listing.key_count}
                                                </div>
                                            </div>
                                        )}
                                        {/* Schlüssel-Features */}
                                        {(listing.has_handsender || listing.has_keyless_transponder || listing.has_immobilizer_card) && (
                                            <div className="md:col-span-2">
                                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Schlüssel-Ausstattung</Label>
                                                <div className="mt-1.5 flex flex-wrap gap-2">
                                                    {listing.has_handsender && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-xs font-bold"><BadgeCheck className="w-3 h-3" /> Handsender</span>}
                                                    {listing.has_keyless_transponder && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-xs font-bold"><BadgeCheck className="w-3 h-3" /> Keyless-Go-Transponder</span>}
                                                    {listing.has_immobilizer_card && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-xs font-bold"><BadgeCheck className="w-3 h-3" /> Sicherungskarte / Wegfahrsperren-Schlüssel</span>}
                                                </div>
                                            </div>
                                        )}
                                        {/* Papiere */}
                                        {(listing.has_coc || listing.has_zb1 || listing.has_zb2) && (
                                            <div className="md:col-span-2" data-testid="detail-papers">
                                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Fahrzeugpapiere</Label>
                                                <div className="mt-1.5 flex flex-wrap gap-2">
                                                    {listing.has_coc && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-xs font-bold"><FileText className="w-3 h-3" /> COC vorhanden</span>}
                                                    {listing.has_zb1 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-xs font-bold"><FileText className="w-3 h-3" /> ZB1 (Zulassung)</span>}
                                                    {listing.has_zb2 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-sm text-xs font-bold"><FileText className="w-3 h-3" /> ZB2 (Fahrzeugbrief)</span>}
                                                </div>
                                            </div>
                                        )}
                                        {/* Dokumente */}
                                        {listing.document_urls && listing.document_urls.length > 0 && (
                                            <div className="md:col-span-2" data-testid="detail-documents">
                                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 block">Dokumente</Label>
                                                {isPrivileged ? (
                                                    <ul className="space-y-1.5">
                                                        {listing.document_urls.map((doc, idx) => {
                                                            const url = typeof doc === "string" ? doc : doc.url;
                                                            const name = typeof doc === "string" ? url.split("/").pop() : (doc.name || url.split("/").pop());
                                                            return (
                                                                <li key={idx}>
                                                                    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-sm text-sm hover:bg-[#111827] hover:text-white transition-colors" data-testid={`detail-doc-${idx}`}>
                                                                        <Download className="w-3.5 h-3.5" />
                                                                        <span>{name}</span>
                                                                    </a>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                ) : (
                                                    <div className="inline-flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900" data-testid="detail-doc-locked">
                                                        <Lock className="w-3.5 h-3.5" />
                                                        <span>
                                                            <strong>{listing.document_urls.length}</strong> Dokument{listing.document_urls.length !== 1 ? "e" : ""} vorhanden — Download nach Beginn des Transaktions-Prozesses freigeschaltet.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
                                        Bitte anmelden, um VIN, Papier-Status und Dokumente einzusehen.
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    {/* Video — Phase 3.51 */}
                    {listing.video_url && (
                        <div className="bg-white border border-gray-200 rounded-sm p-6 md:p-8" data-testid="listing-video-section">
                            <h2 className="font-display text-2xl font-black mb-4 tracking-tight flex items-center gap-2">
                                <Video className="w-5 h-5 text-[#FFCC00]" /> Video
                            </h2>
                            {(() => {
                                const url = listing.video_url;
                                const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{6,})/);
                                const vimeo = url.match(/vimeo\.com\/(\d+)/);
                                if (yt) {
                                    return (
                                        <div className="aspect-video w-full bg-black rounded-sm overflow-hidden">
                                            <iframe
                                                src={`https://www.youtube.com/embed/${yt[1]}`}
                                                title="Video"
                                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                allowFullScreen
                                                className="w-full h-full"
                                                data-testid="listing-video-iframe"
                                            />
                                        </div>
                                    );
                                }
                                if (vimeo) {
                                    return (
                                        <div className="aspect-video w-full bg-black rounded-sm overflow-hidden">
                                            <iframe
                                                src={`https://player.vimeo.com/video/${vimeo[1]}`}
                                                title="Video"
                                                allow="autoplay; fullscreen; picture-in-picture"
                                                allowFullScreen
                                                className="w-full h-full"
                                                data-testid="listing-video-iframe"
                                            />
                                        </div>
                                    );
                                }
                                return (
                                    <a
                                        href={url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-sm font-bold text-[#111827] underline underline-offset-4"
                                        data-testid="listing-video-link"
                                    >
                                        <Video className="w-4 h-4" /> Video ansehen
                                    </a>
                                );
                            })()}
                        </div>
                    )}

                    {/* Sharing — Phase 3.52 */}
                    {listing.sharing_enabled && (
                        <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-6 md:p-8" data-testid="listing-sharing-section">
                            <div className="flex items-center gap-2 mb-4">
                                <Bike className="w-6 h-6 text-[#FFCC00]" />
                                <h2 className="font-display text-2xl font-black tracking-tight">Auch mietbar / Sharing</h2>
                            </div>
                            <p className="text-sm text-gray-600 mb-5">
                                Dieses Fahrzeug kann gemietet werden — tage-, wochen-, monats- oder saisonweise. Kontaktiere den Anbieter für Verfügbarkeiten.
                            </p>
                            {/* Jun-2026 (User-Wunsch) — Tarif-Tabelle mit Ersparnis-Hinweis */}
                            {(() => {
                                const day = Number(listing.sharing_price_per_day) || 0;
                                const saving = (price, mult) => (day > 0 && price > 0 && price < day * mult)
                                    ? Math.round((1 - price / (day * mult)) * 100) : 0;
                                let seasonDays = 0;
                                if (listing.sharing_season_from && listing.sharing_season_to) {
                                    const a = new Date(listing.sharing_season_from), b = new Date(listing.sharing_season_to);
                                    seasonDays = Math.max(0, Math.round((b - a) / 86400000) + 1);
                                }
                                const rows = [
                                    listing.sharing_price_per_day != null && { key: "day", label: "Pro Tag", price: listing.sharing_price_per_day, hint: null },
                                    listing.sharing_price_per_week != null && { key: "week", label: "Pro Woche", price: listing.sharing_price_per_week, hint: saving(Number(listing.sharing_price_per_week), 7) },
                                    listing.sharing_price_per_month != null && { key: "month", label: "Pro Monat", price: listing.sharing_price_per_month, hint: saving(Number(listing.sharing_price_per_month), 30) },
                                    listing.sharing_price_per_season != null && {
                                        key: "season", label: "Pro Saison", price: listing.sharing_price_per_season,
                                        hint: seasonDays ? saving(Number(listing.sharing_price_per_season), seasonDays) : 0,
                                        sub: (listing.sharing_season_from && listing.sharing_season_to)
                                            ? `${new Date(listing.sharing_season_from).toLocaleDateString("de-DE")} – ${new Date(listing.sharing_season_to).toLocaleDateString("de-DE")}` : null,
                                    },
                                ].filter(Boolean);
                                if (!rows.length) return null;
                                return (
                                    <div className="rounded-sm border border-gray-200 overflow-hidden mb-5" data-testid="sharing-rate-table">
                                        {rows.map((r, i) => (
                                            <div key={r.key} className={`flex items-center justify-between gap-4 px-4 py-3 ${i % 2 ? "bg-gray-50" : "bg-white"}`} data-testid={`sharing-rate-${r.key}`}>
                                                <div>
                                                    <div className="font-bold text-[#111827] text-sm">{r.label}</div>
                                                    {r.sub && <div className="text-xs text-gray-500">{r.sub}</div>}
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-black text-lg text-[#111827] font-mono-num">{formatPrice(r.price)}</div>
                                                    {r.hint > 0 && (
                                                        <div className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm" data-testid={`sharing-saving-${r.key}`}>
                                                            −{r.hint}% ggü. {r.key === "week" ? "7×" : r.key === "month" ? "30×" : `${seasonDays}×`} Tagespreis
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                                {listing.sharing_deposit_eur != null && (
                                    <div className="flex justify-between py-3 border-b border-gray-100 text-sm" data-testid="sharing-deposit">
                                        <span className="text-gray-500 font-medium">Kaution</span>
                                        <span className="font-semibold text-[#111827] font-mono-num">{formatPrice(listing.sharing_deposit_eur)}</span>
                                    </div>
                                )}
                                {(listing.sharing_min_days || listing.sharing_max_days) && (
                                    <div className="flex justify-between py-3 border-b border-gray-100 text-sm" data-testid="sharing-duration">
                                        <span className="text-gray-500 font-medium">Mietdauer</span>
                                        <span className="font-semibold text-[#111827]">
                                            {listing.sharing_min_days || 1}–{listing.sharing_max_days || "?"} Tage
                                        </span>
                                    </div>
                                )}
                                {listing.sharing_available_weekdays?.length > 0 && (
                                    <div className="flex justify-between py-3 border-b border-gray-100 text-sm" data-testid="sharing-weekdays">
                                        <span className="text-gray-500 font-medium">Verfügbare Tage</span>
                                        <span className="font-semibold text-[#111827]">{listing.sharing_available_weekdays.join(", ")}</span>
                                    </div>
                                )}
                                {listing.sharing_pickup_address && (
                                    <div className="md:col-span-2 flex justify-between py-3 border-b border-gray-100 text-sm" data-testid="sharing-address">
                                        <span className="text-gray-500 font-medium flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Abholadresse</span>
                                        <span className="font-semibold text-[#111827] text-right">{listing.sharing_pickup_address}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Right: Seller card + contact */}
                <div className="space-y-4">
                    {isAuction && (
                        <AuctionPanel listing={listing} onUpdate={refresh} />
                    )}
                    {!isAuction && (
                        <>
                            <ReservationPanel listing={listing} onUpdate={refresh} />
                            <OfferPanel listing={listing} onUpdate={refresh} />
                            <TradeInPanel listing={listing} onUpdate={refresh} />
                        </>
                    )}
                    <div className="bg-white border border-gray-200 rounded-sm p-6 sticky top-24">
                        <div className="flex items-center gap-3 mb-4">
                            <div className={`w-14 h-14 rounded-sm flex items-center justify-center font-display font-black text-2xl ${isB2B ? "bg-[#111827] text-[#FFCC00]" : "bg-gray-200 text-gray-700"}`}>
                                {(owner?.company_name || owner?.name || "?").charAt(0)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <Badge className={isB2B ? "bg-[#111827] text-white hover:bg-[#111827] uppercase tracking-wide font-bold rounded-sm" : "bg-gray-200 text-gray-800 hover:bg-gray-200 uppercase tracking-wide font-bold rounded-sm"}>
                                        {isB2B ? "Händler" : "Privat"}
                                    </Badge>
                                    {owner?.verified && <Shield className="w-4 h-4 text-green-600" />}
                                </div>
                                <div className="font-display font-bold text-lg truncate" data-testid="seller-name">{owner?.company_name || owner?.name}</div>
                                {(owner?.contact_first_name || owner?.contact_last_name) && (
                                    <div className="text-xs text-gray-600 truncate" data-testid="seller-contact-person">
                                        Ansprechpartner: <strong className="text-gray-800">{[owner.contact_first_name, owner.contact_last_name].filter(Boolean).join(" ")}</strong>
                                    </div>
                                )}
                                {(owner?.rating_count > 0 || owner?.rating) && (
                                    <Link
                                        to={`/dealer/${owner.id}#reviews`}
                                        className="inline-flex items-center gap-1 text-sm hover:underline"
                                        data-testid="seller-rating-badge"
                                        title={owner.rating_count ? `${owner.rating_count} Bewertung${owner.rating_count === 1 ? "" : "en"}` : ""}
                                    >
                                        <Star className="w-3.5 h-3.5 fill-[#FFCC00] text-[#FFCC00]" />
                                        <span className="font-mono-num font-semibold">
                                            {owner.rating_avg ?? owner.rating}
                                        </span>
                                        {owner.rating_count > 0 && (
                                            <span className="text-xs text-gray-500">({owner.rating_count})</span>
                                        )}
                                    </Link>
                                )}
                            </div>
                        </div>

                        <div className="space-y-2 text-sm text-gray-700 mb-5">
                            {owner?.city && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /> {owner.city}</div>}
                            {/* Mai-2026 (User-Wunsch 21 May Iteration 14) — Telefonnummer maskiert.
                                Klick „Aufdecken" zeigt die vollständige Nummer; E-Mail komplett ausgeblendet zugunsten Direkt-Chat. */}
                            {listing.tracking_number ? (
                                <div className="flex flex-col gap-1" data-testid="phone-row">
                                    <div className="flex items-center gap-2">
                                        <Phone className="w-4 h-4 text-gray-400" />
                                        <a href={`tel:${listing.tracking_number.replace(/\s/g, "")}`} className="font-bold text-[#111827] hover:underline" data-testid="phone-display">{listing.tracking_number}</a>
                                    </div>
                                    <div className="text-[11px] text-gray-400 ml-6">Anruf wird diesem Inserat zugeordnet</div>
                                </div>
                            ) : owner?.phone && (
                                <div className="flex items-center gap-2" data-testid="phone-row">
                                    <Phone className="w-4 h-4 text-gray-400" />
                                    <span data-testid="phone-display">{phoneRevealed ? owner.phone : maskPhone(owner.phone)}</span>
                                    {!phoneRevealed && (
                                        <button
                                            type="button"
                                            onClick={() => setPhoneRevealed(true)}
                                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 inline-flex items-center gap-1"
                                            data-testid="phone-reveal-btn"
                                        >
                                            <Eye className="w-3 h-3" /> Aufdecken
                                        </button>
                                    )}
                                </div>
                            )}
                            <div className="mt-3">
                                <CallbackRequestButton listing={listing} user={user} />
                            </div>
                            {/* Feb-2026 (User-Wunsch) — „Per WhatsApp kontaktieren", wenn der
                                Händler WhatsApp aktiviert + eine Nummer hinterlegt hat. Öffnet
                                wa.me mit vorbefüllter Nachricht (Inseratstitel + Link). */}
                            {!isOwnerListing && owner?.whatsapp_enabled && owner?.whatsapp_number && (
                                <div className="mt-3" data-testid="whatsapp-contact-block">
                                    <a
                                        href={`https://wa.me/${owner.whatsapp_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Hallo, ich interessiere mich für Ihr Inserat „${listing.title}" auf twowheels.info: ${typeof window !== "undefined" ? window.location.href : ""}`)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => { api.post(`/listings/${listing.id}/whatsapp-lead`).catch(() => {}); }}
                                        className="inline-flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1FB855] text-white font-bold text-sm uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors"
                                        data-testid="whatsapp-contact-btn"
                                    >
                                        <MessageCircle className="w-4 h-4" /> Per WhatsApp kontaktieren
                                    </a>
                                </div>
                            )}
                            {isOwnerListing && (
                                <div className="mt-3">
                                    <TrackingNumberManager listing={listing} onUpdate={refresh} />
                                </div>
                            )}
                        </div>

                        <div className="border-t border-gray-100 pt-5">
                            {(() => {
                                const chatEnabled = listing.allow_chat !== false && owner?.allow_chat !== false;
                                const isLoggedIn = !!(user && user !== false);
                                const accBox = selectedAcc.length > 0 && (
                                    <div className="mb-2 border border-[#FFCC00]/70 bg-amber-50/70 rounded-sm p-2" data-testid="selected-accessories-box">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1.5 flex items-center gap-1">
                                            <ShoppingBag className="w-3 h-3" /> Zubehör in dieser Anfrage ({selectedAcc.length})
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {selectedAcc.map((a) => (
                                                <span key={a.id} className="inline-flex items-center gap-1 bg-white border border-gray-200 rounded-sm px-1.5 py-0.5 text-[10px] font-semibold text-gray-700" data-testid={`selected-acc-chip-${a.id}`}>
                                                    {a.name.length > 28 ? `${a.name.slice(0, 28)}…` : a.name} · {formatPrice(a.price_gross)}
                                                    <button onClick={() => setSelectedAcc((p) => p.filter((x) => x.id !== a.id))} className="text-gray-400 hover:text-red-600 font-bold" data-testid={`selected-acc-remove-${a.id}`} aria-label="Zubehör entfernen">×</button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                                if (!chatEnabled) {
                                    return (
                                        <>
                                            {accBox}
                                            <div className="mb-3" data-testid="quick-actions-grid">
                                                <SellerEmailButton listing={listing} extraText={accLines} />
                                            </div>
                                            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-sm p-3" data-testid="chat-disabled-note">
                                                Der Direkt-Chat ist für dieses Inserat deaktiviert. Nutzen Sie „Einfache E-Mail" oben — Ihre Anfrage landet direkt beim Verkäufer.
                                            </div>
                                        </>
                                    );
                                }
                                return (
                                    <>
                                        {/* Feb-2026 (User-Wunsch): „Einfache E-Mail" ÜBER die Chat-Überschrift
                                            gezogen — eigenständige Kontaktaktion oberhalb des Direkt-Chats. */}
                                        <div className="mb-3" data-testid="quick-actions-grid">
                                            <SellerEmailButton listing={listing} extraText={accLines} />
                                        </div>
                                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-2">
                                            <Send className="w-3 h-3" /> Direkt-Chat mit Verkäufer
                                        </div>
                                        <div className="mb-3 text-[10px] bg-amber-50 border-l-2 border-amber-400 px-2 py-1.5 text-amber-800 rounded-sm" data-testid="chat-dsgvo-banner">
                                            🛡 Hinweis: Zur Streitschlichtung kann der Plattform-Betreiber Chats einsehen.
                                        </div>
                                        {accBox}
                                        <Textarea
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder={`Hallo, ist die ${listing.brand} ${listing.model} noch verfügbar?`}
                                            className="rounded-sm mb-3 min-h-[100px]"
                                            data-testid="message-textarea"
                                        />
                                        <Button
                                            onClick={sendMessage}
                                            disabled={sending || (!message.trim() && selectedAcc.length === 0)}
                                            className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm h-11"
                                            data-testid="send-message-btn"
                                        >
                                            <Send className="w-4 h-4 mr-2" />
                                            {sending ? "Senden..." : "Anfrage senden"}
                                        </Button>
                                        {!isLoggedIn && (
                                            <p className="text-xs text-gray-500 mt-2 text-center">
                                                Du musst <Link to="/login" className="underline font-semibold">angemeldet</Link> sein.
                                            </p>
                                        )}
                                    </>
                                );
                            })()}
                        </div>

                        {isB2B && (
                            <div className="mt-5 border-t border-gray-100 pt-5">
                                <Link to={`/dealer/${owner.id}`} className="text-sm font-bold underline decoration-2 underline-offset-4" data-testid="view-dealer-link">
                                    Alle Inserate dieses Händlers →
                                </Link>
                            </div>
                        )}

                        <div className="mt-4 border-t border-gray-100 pt-4">
                            <button
                                type="button"
                                onClick={openImprint}
                                className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-700 hover:text-[#111827] underline underline-offset-2"
                                data-testid="listing-imprint-link"
                            >
                                <FileText className="w-3.5 h-3.5" />
                                Verantwortlich für dieses Inserat
                            </button>
                        </div>
                    </div>

                    <div className="bg-[#111827] text-white rounded-sm p-5 text-xs">
                        <div className="flex items-center gap-2 font-bold uppercase tracking-wider mb-2"><Shield className="w-4 h-4 text-[#FFCC00]" /> Sicher kaufen</div>
                        <p className="text-white/70 leading-relaxed">
                            Vereinbare Besichtigung & Probefahrt. Niemals Geld im Voraus überweisen.
                        </p>
                    </div>
                </div>
            </div>

            {imprintOpen && imprintData && (
                <div
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
                    onClick={() => setImprintOpen(false)}
                    data-testid="listing-imprint-modal-overlay"
                >
                    <div
                        className="bg-white border-2 border-[#FFCC00] rounded-sm w-full max-w-xl max-h-[90vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="listing-imprint-modal"
                    >
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-[#FFCC00]" />
                                <h3 className="font-display font-bold text-lg">Verantwortlich für dieses Inserat</h3>
                            </div>
                            <button
                                onClick={() => setImprintOpen(false)}
                                className="text-gray-400 hover:text-gray-700 p-1"
                                data-testid="listing-imprint-modal-close"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="px-6 py-4 overflow-y-auto flex-1">
                            <pre
                                className="whitespace-pre-line font-sans text-sm text-[#111827] leading-relaxed"
                                data-testid="listing-imprint-text"
                            >{imprintData.imprint_text}</pre>
                            {imprintData.is_auto_generated && (
                                <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-sm text-[11px] text-gray-600">
                                    Hinweis: Dieser Text wurde automatisch aus den Anbieter-Stammdaten generiert (der Anbieter hat noch keine eigene Fassung bestätigt).
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Jun-2026 (User-Wunsch) — Dauerhafte „Mehr von diesem Händler"-Leiste auf
                jeder Detailseite (Cross-Selling; bei verkauften Fzg. Ziel des CTA-Buttons). */}
            {similar.length > 0 && (
                <section id="similar-vehicles" className="mt-10 scroll-mt-24" data-testid="similar-vehicles-section">
                    <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Bike className="w-5 h-5 text-[#111827]" />
                            <h2 className="text-lg font-black text-[#111827]">Mehr von diesem Händler</h2>
                        </div>
                        {listing.owner_id && (
                            <Link
                                to={`/dealer/${listing.owner_id}`}
                                className="text-sm font-bold underline decoration-2 underline-offset-4 text-[#111827] hover:text-[#111827]/70 inline-flex items-center gap-1"
                                data-testid="similar-view-all-dealer-link"
                            >
                                Alle Inserate dieses Händlers <ChevronRight className="w-4 h-4" />
                            </Link>
                        )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {similar.map((s) => {
                            const sImg = normalizeImageUrl(s.images?.[0]);
                            return (
                                <Link
                                    key={s.id}
                                    to={`/listing/${s.id}`}
                                    className="group block bg-white border border-gray-200 rounded-sm overflow-hidden hover:border-[#111827] hover:shadow-md transition-all"
                                    data-testid={`similar-vehicle-${s.id}`}
                                >
                                    <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                                        {sImg ? (
                                            <img src={sImg} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-gray-300"><Bike className="w-8 h-8" /></div>
                                        )}
                                    </div>
                                    <div className="p-3">
                                        <div className="font-bold text-sm text-[#111827] truncate">{s.title || `${s.brand || ""} ${s.model || ""}`.trim()}</div>
                                        <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                                            {[s.year, s.mileage != null ? formatMileage(s.mileage) : null].filter(Boolean).join(" · ") || "—"}
                                        </div>
                                        <div className="font-display font-black text-lg text-[#111827] font-mono-num mt-1">{formatPrice(s.price)}</div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Jun-2026 (User-Wunsch) — Zubehör-Schaufenster: lagerndes Zubehör des
                Händlers als Kaufempfehlung direkt am Fahrzeug (VK = Hersteller-UVP). */}
            {accessories.length > 0 && (
                <section className="mt-10" data-testid="accessory-showcase-section">
                    <div className="flex items-center gap-2 mb-1">
                        <ShoppingBag className="w-5 h-5 text-[#111827]" />
                        <h2 className="text-lg font-black text-[#111827]">Passendes Zubehör vom Händler</h2>
                    </div>
                    <p className="text-xs text-gray-500 mb-4">Sofort verfügbar am Standort — einfach bei der Anfrage oder Abholung dazubestellen.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {accessories.map((a) => (
                            <div
                                key={a.id}
                                className="bg-white border border-gray-200 rounded-sm p-4 flex flex-col gap-2 hover:border-[#FFCC00] transition-colors"
                                data-testid={`accessory-item-${a.id}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <ShoppingBag className="w-4 h-4 text-[#FFCC00] shrink-0 mt-0.5" />
                                    {a.matched && (
                                        <span className="text-[9px] font-black uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm">Passend</span>
                                    )}
                                </div>
                                <div className="font-bold text-sm text-[#111827] leading-snug line-clamp-2" title={a.name}>{a.name}</div>
                                {a.manufacturer && <div className="text-[11px] text-gray-500 truncate">{a.manufacturer}</div>}
                                <div className="font-display font-black text-lg text-[#111827] font-mono-num mt-auto">{formatPrice(a.price_gross)}</div>
                                {a.in_stock === false ? (
                                    <div className="text-[10px] text-amber-700 font-bold uppercase tracking-wider" data-testid={`accessory-order-badge-${a.id}`}>Auf Bestellung · UVP-Preis</div>
                                ) : (
                                    <div className="text-[10px] text-emerald-700 font-bold uppercase tracking-wider">Auf Lager · UVP-Preis</div>
                                )}
                                <button
                                    onClick={() => toggleAccessory(a)}
                                    className={`mt-1 w-full text-[11px] font-bold uppercase tracking-wider rounded-sm py-1.5 border transition-colors ${selectedAcc.some((x) => x.id === a.id) ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-[#111827] border-gray-300 hover:border-[#111827]"}`}
                                    data-testid={`accessory-add-${a.id}`}
                                >
                                    {selectedAcc.some((x) => x.id === a.id) ? "✓ In der Anfrage" : "+ Zur Anfrage"}
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            )}
            {/* Feb-2026 (User-Wunsch 26 May v2): Anonymes Probefahrt-Modal — bleibt für
                Owner/Admin Test-Flows verfügbar. UI-Trigger wurde in der Käufer-Sidebar
                durch das ReservationPanel ersetzt (User-Wunsch 27 May). */}
            <TestRideRequestModal
                open={testRideModalOpen}
                onClose={() => setTestRideModalOpen(false)}
                listingId={id}
                listingTitle={listing?.title}
                defaults={user && user !== false ? {
                    first_name: user.first_name || user.contact_first_name || "",
                    last_name: user.last_name || user.contact_last_name || "",
                    email: user.email || "",
                    phone: user.phone_mobile || user.phone || "",
                } : {}}
            />
        </div>
    );
}

/**
 * ShareButton — generates a share URL for the current listing including the
 * referrer's transaction code when the visitor is logged in. The code is the
 * caller's customer_number (B2B and B2C both have one). On successful purchase
 * the marketing/attribution system can credit the referrer.
 *
 * Tries the native Web Share API first (mobile), falls back to a copy-link popup.
 */
function ShareButton({ listing, user }) {
    const [open, setOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    // Feb-2026 (User-Wunsch) — KI-Captions direkt im Teilen-Dialog: Händler
    // generieren fertige Social-Captions + Hashtags und kopieren sie mit einem
    // Klick, bevor sie über die Plattform-Buttons posten.
    const [capLoading, setCapLoading] = useState(false);
    const [capData, setCapData] = useState(null);
    const [capCopied, setCapCopied] = useState(null);
    const isDealer = user?.role === "b2b";

    const generateCaptions = async () => {
        setCapLoading(true);
        setCapData(null);
        try {
            const { data } = await api.post("/listings/ai-social-captions", listing);
            setCapData(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Captions konnten nicht generiert werden.");
        } finally {
            setCapLoading(false);
        }
    };

    const copyCaption = (txt, key) => {
        if (!txt) return;
        navigator.clipboard?.writeText(txt).then(
            () => { setCapCopied(key); setTimeout(() => setCapCopied(null), 2000); toast.success("Caption kopiert"); },
            () => toast.error("Kopieren fehlgeschlagen"),
        );
    };

    // Marketing code uses the existing H_<customer_number> dealer scheme and
    // a parallel K_<customer_number> scheme for private customers. Vermittler
    // accounts re-use the H_ prefix because their CN is treated the same way
    // on the attribution backend.
    const code = (() => {
        if (!user?.customer_number) return null;
        const prefix = user.role === "b2c" ? "K_" : "H_";
        return `${prefix}${user.customer_number}`;
    })();

    const shareUrl = (() => {
        const base = typeof window !== "undefined"
            ? `${window.location.origin}/listing/${listing.id}`
            : `/listing/${listing.id}`;
        return code ? `${base}?ref=${encodeURIComponent(code)}` : base;
    })();

    const title = `${listing.brand} ${listing.model} — ${listing.year}`;
    const text = `Schau dir dieses Fahrzeug auf twowheels.info an: ${title}`;
    // Phase 4.12 — reicherer Mailto-Body mit Eckdaten
    const priceFmt = listing.price ? `${Number(listing.price).toLocaleString("de-DE")} €` : "auf Anfrage";
    const mileageFmt = listing.mileage ? `${Number(listing.mileage).toLocaleString("de-DE")} km` : "—";
    const emailBody = [
        `Hi,`,
        ``,
        `ich habe dieses Fahrzeug auf twowheels.info gesehen und dachte, es könnte dich interessieren:`,
        ``,
        `${title}`,
        `Preis: ${priceFmt}`,
        `Kilometerstand: ${mileageFmt}`,
        `Zustand: ${listing.condition || "—"}`,
        ``,
        `Hier ansehen:`,
        shareUrl,
    ].join("\n");
    const emailHref = `mailto:?subject=${encodeURIComponent(title + " — twowheels.info")}&body=${encodeURIComponent(emailBody)}`;

    const onClick = async () => {
        // Native share is most ergonomic on mobile. Web Share API is gated to
        // secure contexts, so we always have the copy-link fallback below.
        if (typeof navigator !== "undefined" && navigator.share) {
            try {
                await navigator.share({ title, text, url: shareUrl });
                return;
            } catch {
                /* user cancelled — fall through to dialog */
            }
        }
        setOpen(true);
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* clipboard blocked — user can still select the field manually */
        }
    };

    return (
        <>
            <Button
                variant="outline"
                onClick={onClick}
                className="rounded-sm border-2 border-[#111827]"
                data-testid="detail-share-btn"
            >
                <Share2 className="w-4 h-4 mr-2" />
                Weiterempfehlen
            </Button>
            {open && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="share-dialog">
                    <div className="bg-white rounded-sm max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="font-display text-xl font-black mb-2 flex items-center gap-2">
                            <Share2 className="w-5 h-5 text-[#FFCC00]" /> Inserat weiterempfehlen
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {code
                                ? <>Dein Empfehlungs-Code <span className="font-mono-num bg-[#FFCC00]/30 px-2 py-0.5 rounded-sm font-bold">{code}</span> ist im Link enthalten — bei einem Verkauf über diesen Link kannst du belohnt werden.</>
                                : <>Logge dich ein, um deinen Empfehlungs-Code automatisch im Link einzubetten.</>
                            }
                        </p>
                        <div className="flex gap-2">
                            <input
                                readOnly
                                value={shareUrl}
                                onFocus={(e) => e.target.select()}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-sm text-xs font-mono-num"
                                data-testid="share-url-input"
                            />
                            <Button onClick={copy} className="bg-[#111827] text-white rounded-sm" data-testid="share-copy-btn">
                                {copied ? <Check className="w-4 h-4" /> : <CopyIcon className="w-4 h-4" />}
                            </Button>
                        </div>
                        <div className="mt-4" data-testid="share-social-grid">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Auf Social Media posten</div>
                            <div className="grid grid-cols-3 gap-2">
                                <a
                                    href={`https://wa.me/?text=${encodeURIComponent(`${text}\n${shareUrl}`)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 transition-colors"
                                    data-testid="share-whatsapp"
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">WhatsApp</span>
                                </a>
                                <a
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-[#1877F2] text-white rounded-sm hover:brightness-110 transition-all"
                                    data-testid="share-facebook"
                                >
                                    <Facebook className="w-5 h-5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Facebook</span>
                                </a>
                                <a
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-[#111827] text-white rounded-sm hover:bg-black transition-colors"
                                    data-testid="share-x"
                                >
                                    <Twitter className="w-5 h-5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">X / Twitter</span>
                                </a>
                                <a
                                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-[#0A66C2] text-white rounded-sm hover:brightness-110 transition-all"
                                    data-testid="share-linkedin"
                                >
                                    <Linkedin className="w-5 h-5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">LinkedIn</span>
                                </a>
                                <a
                                    href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-[#229ED9] text-white rounded-sm hover:brightness-110 transition-all"
                                    data-testid="share-telegram"
                                >
                                    <Send className="w-5 h-5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">Telegram</span>
                                </a>
                                <a
                                    href={emailHref}
                                    className="flex flex-col items-center justify-center gap-1 px-2 py-3 bg-gray-700 text-white rounded-sm hover:bg-gray-800 transition-colors"
                                    data-testid="share-email"
                                >
                                    <Mail className="w-5 h-5" />
                                    <span className="text-[10px] font-bold uppercase tracking-wider">E-Mail</span>
                                </a>
                            </div>
                            {isDealer && (
                                <div className="mt-4 border-t border-gray-100 pt-3" data-testid="share-ai-captions">
                                    {!capData && (
                                        <button
                                            type="button"
                                            onClick={generateCaptions}
                                            disabled={capLoading}
                                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-sm bg-[#111827] text-[#FFCC00] text-xs font-bold uppercase tracking-wider hover:bg-black disabled:opacity-50"
                                            data-testid="share-generate-captions-btn"
                                        >
                                            {capLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                            {capLoading ? "Generiere Captions…" : "KI-Caption für Social Media erstellen"}
                                        </button>
                                    )}
                                    {capData && (
                                        <div className="space-y-2" data-testid="share-captions-result">
                                            {[
                                                { key: "instagram", label: "Instagram", Icon: Instagram, accent: "#E1306C" },
                                                { key: "facebook", label: "Facebook", Icon: Facebook, accent: "#1877F2" },
                                                { key: "tiktok", label: "TikTok", Icon: Music2, accent: "#111827" },
                                            ].filter(({ key }) => capData[key]).map(({ key, label, Icon, accent }) => (
                                                <div key={key} className="border border-gray-200 rounded-sm" data-testid={`share-caption-${key}`}>
                                                    <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-100" style={{ backgroundColor: `${accent}10` }}>
                                                        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: accent }}>
                                                            <Icon className="w-3.5 h-3.5" /> {label}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => copyCaption(capData[key], key)}
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black"
                                                            data-testid={`share-caption-copy-${key}`}
                                                        >
                                                            {capCopied === key ? <Check className="w-3 h-3" /> : <CopyIcon className="w-3 h-3" />} Kopieren
                                                        </button>
                                                    </div>
                                                    <p className="px-2.5 py-2 text-[13px] text-gray-900 whitespace-pre-line leading-snug max-h-28 overflow-y-auto">{capData[key]}</p>
                                                </div>
                                            ))}
                                            {Array.isArray(capData.hashtags) && capData.hashtags.length > 0 && (
                                                <div className="border border-gray-200 rounded-sm" data-testid="share-caption-hashtags">
                                                    <div className="flex items-center justify-between px-2.5 py-1.5 border-b border-gray-100 bg-gray-100">
                                                        <span className="text-[11px] font-bold uppercase tracking-wider text-gray-700">Hashtags</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => copyCaption(capData.hashtags.join(" "), "hashtags")}
                                                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black"
                                                            data-testid="share-caption-copy-hashtags"
                                                        >
                                                            {capCopied === "hashtags" ? <Check className="w-3 h-3" /> : <CopyIcon className="w-3 h-3" />} Kopieren
                                                        </button>
                                                    </div>
                                                    <div className="px-2.5 py-2 flex flex-wrap gap-1">
                                                        {capData.hashtags.map((h) => (
                                                            <span key={h} className="text-[11px] font-mono text-[#111827] bg-gray-100 px-1.5 py-0.5 rounded-sm">{h}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {capData.mocked && (
                                                <p className="text-[11px] text-gray-500 italic">Fallback-Modus aktiv — Captions sind generisch.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="flex mt-3">
                                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm ml-auto" data-testid="share-close-btn">
                                    Schließen
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}



function EmailOfferButton({ listingId, user }) {
    const { setSession } = useAuth();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState("");
    const [msg, setMsg] = useState("");
    const [ccSelf, setCcSelf] = useState(false);
    const [recents, setRecents] = useState([]);
    const [sending, setSending] = useState(false);
    const [gFirst, setGFirst] = useState("");
    const [gLast, setGLast] = useState("");
    const [gEmail, setGEmail] = useState("");
    const [gPhone, setGPhone] = useState("");
    const isLoggedIn = !!(user && user.id);

    useEffect(() => {
        if (!open || !isLoggedIn) return;
        api.get("/me/offer-recipients")
            .then((r) => setRecents(r.data?.recipients || []))
            .catch(() => setRecents([]));
    }, [open, isLoggedIn]);

    const send = async () => {
        if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
            toast.error("Bitte eine gültige Empfänger-E-Mail eingeben");
            return;
        }
        // Gast ohne Login: zuerst Vorname/Nachname/E-Mail/Handy erfassen → Auto-Login.
        if (!isLoggedIn) {
            const first = gFirst.trim(), last = gLast.trim(), gem = gEmail.trim(), gph = gPhone.trim();
            if (first.length < 2) { toast.error("Bitte Ihren Vornamen eingeben"); return; }
            if (last.length < 2) { toast.error("Bitte Ihren Nachnamen eingeben"); return; }
            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(gem)) { toast.error("Bitte eine gültige E-Mail-Adresse eingeben"); return; }
            if (gph.replace(/[^0-9]/g, "").length < 6) { toast.error("Bitte eine gültige Handynummer eingeben"); return; }
            setSending(true);
            try {
                const { data } = await api.post("/auth/guest", { name: `${first} ${last}`.trim(), email: gem, phone: gph || null });
                if (data?.existing) {
                    toast.error("Für diese E-Mail existiert bereits ein Konto — bitte einloggen.");
                    setSending(false);
                    return;
                }
                if (data?.token && data?.user) setSession(data.user, data.token);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Anmeldung fehlgeschlagen");
                setSending(false);
                return;
            }
        }
        setSending(true);
        try {
            const { data } = await api.post(`/listings/${listingId}/offer/email`, {
                recipient_email: email.trim(),
                message: msg.trim() || undefined,
                cc_self: ccSelf,
            });
            const ccNote = data.cc_self ? " (Kopie an Sie)" : "";
            if (data.mocked) {
                toast.success(`Angebot vorbereitet für ${data.recipient}${ccNote}. Der Händler wurde in seinem Postfach informiert.`);
            } else {
                toast.success(`Angebot an ${data.recipient}${ccNote} gesendet. Der Händler wurde informiert.`);
            }
            setOpen(false);
            setEmail(""); setMsg("");
            setGFirst(""); setGLast(""); setGEmail(""); setGPhone("");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Senden fehlgeschlagen");
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <Button type="button" variant="outline" onClick={() => setOpen(true)} className="rounded-sm border-2 border-[#111827]" data-testid="detail-email-offer-btn">
                <Mail className="w-4 h-4 mr-2" /> Per E-Mail senden
            </Button>
            {open && (
                <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="email-offer-modal">
                    <div className="bg-white rounded-sm max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="text-lg font-black text-[#111827] mb-1">Angebot per E-Mail senden</div>
                        <p className="text-xs text-gray-500 mb-4">Das gebrandete Angebots-PDF (mit QR-Code) wird als Anhang versendet. Der Händler wird über Ihre Anfrage in seinem Postfach informiert.</p>
                        {!isLoggedIn && (
                            <div className="space-y-3 pb-4 mb-4 border-b border-gray-100" data-testid="email-offer-guest-fields">
                                <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-sm p-2.5 text-xs text-[#92400E]">
                                    Kein Konto nötig — wir legen automatisch eines für Sie an, damit Sie die Antwort verfolgen können.
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Vorname</label>
                                        <Input value={gFirst} onChange={(e) => setGFirst(e.target.value)} placeholder="Max" className="rounded-sm h-11" data-testid="email-offer-first-input" />
                                    </div>
                                    <div>
                                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Nachname</label>
                                        <Input value={gLast} onChange={(e) => setGLast(e.target.value)} placeholder="Mustermann" className="rounded-sm h-11" data-testid="email-offer-last-input" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Ihre E-Mail</label>
                                    <Input type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)} placeholder="ihre@email.de" className="rounded-sm h-11" data-testid="email-offer-guest-email-input" />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5 block">Handynummer</label>
                                    <Input type="tel" value={gPhone} onChange={(e) => setGPhone(e.target.value)} placeholder="z.B. 0151 23456789" className="rounded-sm h-11" data-testid="email-offer-guest-phone-input" />
                                </div>
                            </div>
                        )}
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Empfänger-E-Mail</label>
                        <Input type="email" list="offer-recipient-suggestions" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="interessent@example.com" className="mt-1.5 mb-3 rounded-sm" data-testid="email-offer-recipient" />
                        {recents.length > 0 && (
                            <datalist id="offer-recipient-suggestions" data-testid="email-offer-suggestions">
                                {recents.map((r) => <option key={r} value={r} />)}
                            </datalist>
                        )}
                        <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Nachricht (optional)</label>
                        <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Persönliche Nachricht …" className="mt-1.5 mb-3 rounded-sm" rows={3} data-testid="email-offer-message" />
                        {isLoggedIn && (
                            <label className="flex items-center gap-2 mb-4 cursor-pointer text-sm text-gray-700" data-testid="email-offer-cc-self-label">
                                <input type="checkbox" checked={ccSelf} onChange={(e) => setCcSelf(e.target.checked)} className="w-4 h-4 accent-[#FFCC00]" data-testid="email-offer-cc-self" />
                                Kopie an mich (CC)
                            </label>
                        )}
                        <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm" data-testid="email-offer-cancel">Abbrechen</Button>
                            <Button onClick={send} disabled={sending} className="rounded-sm bg-[#111827] text-white hover:bg-[#111827]/90" data-testid="email-offer-send">
                                {sending ? "Senden…" : "Senden"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
